'use client';

import { useCallback, useEffect, useState, type CSSProperties, type RefObject } from 'react';

const GAP = 4;
const MARGIN = 8;
/** Below this much room a dropdown opens upward instead, if there is more room there. */
const MIN_COMFORTABLE = 200;

/**
 * Where a dropdown goes: under its field, or above it when the room below is
 * short, never taller than the room it opens into. `position: fixed`, so it
 * is rendered in a portal and no scrolling form or dialog can clip it
 * (2026-10-24: the country list lost 130 px inside the registration form on a
 * 320 px phone; the specialty list vanished entirely on a 1366×660 laptop).
 */
export function placeAnchoredPanel(
  anchor: { top: number; bottom: number; left: number; width: number },
  viewport: { width: number; height: number },
  wantedHeight: number
): CSSProperties {
  const below = viewport.height - anchor.bottom - GAP - MARGIN;
  const above = anchor.top - GAP - MARGIN;
  const up = below < Math.min(wantedHeight, MIN_COMFORTABLE) && above > below;
  const room = Math.max(0, up ? above : below);
  const width = Math.min(anchor.width, viewport.width - 2 * MARGIN);
  const left = Math.max(MARGIN, Math.min(anchor.left, viewport.width - width - MARGIN));
  return {
    position: 'fixed',
    left,
    width,
    maxHeight: Math.min(wantedHeight, room),
    ...(up ? { bottom: viewport.height - anchor.top + GAP } : { top: anchor.bottom + GAP }),
  };
}

/**
 * Keeps a portalled dropdown attached to its field: `open()` places it (call it
 * from the click that opens it), scrolling and resizing re-place it, `close()`
 * drops it. `style` is null while closed.
 */
export function useAnchoredPanel(anchorRef: RefObject<HTMLElement | null>, wantedHeight: number) {
  const [style, setStyle] = useState<CSSProperties | null>(null);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    setStyle(placeAnchoredPanel(el.getBoundingClientRect(), { width: window.innerWidth, height: window.innerHeight }, wantedHeight));
  }, [anchorRef, wantedHeight]);

  const close = useCallback(() => setStyle(null), []);

  const isOpen = style !== null;
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen, place]);

  return { style, open: place, close };
}
